import React from 'react';
import './FormField.css';

function parseOptions(options) {
    if (Array.isArray(options)) {
        return options;
    }

    if (typeof options === 'string' && options.trim()) {
        try {
            return JSON.parse(options);
        } catch (error) {
            console.error('Failed to parse field options:', error);
        }
    }

    return [];
}

function getFieldValue(value) {
    return value ?? '';
}

function FormField({ field, value, onChange, readOnly = false }) {
    const disabled = readOnly || field.editable === false;
    const requiredMark = field.required ? <span className="form-field__required">*</span> : null;
    const isFullWidth = field.type === 'boolean' || field.type === 'text' || field.name === 'floors';
    const normalizedValue = getFieldValue(value);

    const handleChange = (nextValue) => {
        onChange(field.name, nextValue);
    };

    if (field.name === 'floors') {
        const parts = String(normalizedValue).split('/');
        const floorParts = [parts[0] || '', parts[1] || '', parts[2] || ''];

        const handlePartChange = (index, partValue) => {
            const nextParts = [...floorParts];
            nextParts[index] = partValue;
            handleChange(nextParts.join('/'));
        };

        return (
            <div className={`form-field ${isFullWidth ? 'form-field--full' : ''}`}>
                <label className="form-field__label">
                    {field.label}
                    {requiredMark}
                </label>
                <div className="form-field__split">
                    {['层', '站', '门'].map((partLabel, index) => (
                        <label key={partLabel} className="form-field__split-item">
                            <span>{partLabel}</span>
                            <input
                                type="number"
                                className="app-input"
                                value={floorParts[index]}
                                onChange={(event) => handlePartChange(index, event.target.value)}
                                disabled={disabled}
                                placeholder={partLabel}
                            />
                        </label>
                    ))}
                </div>
                <small className="form-field__hint">按“层/站/门”格式自动组合保存。</small>
            </div>
        );
    }

    if (field.name === 'speed' || field.name === 'weight_limit') {
        const unit = field.name === 'speed' ? 'm/s' : 'kg';
        const displayValue = typeof normalizedValue === 'string' ? normalizedValue.replace(new RegExp(`\\s*${unit}$`), '') : normalizedValue;

        return (
            <div className={`form-field ${isFullWidth ? 'form-field--full' : ''}`}>
                <label className="form-field__label">
                    {field.label}
                    {requiredMark}
                </label>
                <div className="form-field__unit">
                    <input
                        type="number"
                        step="0.01"
                        className="app-input"
                        value={displayValue}
                        onChange={(event) => handleChange(event.target.value)}
                        onBlur={(event) => {
                            const numericValue = event.target.value.trim();
                            if (!numericValue) {
                                handleChange('');
                                return;
                            }

                            handleChange(`${numericValue} ${unit}`);
                        }}
                        disabled={disabled}
                        placeholder={`请输入${field.label}`}
                    />
                    <span>{unit}</span>
                </div>
            </div>
        );
    }

    const renderControl = () => {
        switch (field.type) {
            case 'select': {
                const options = parseOptions(field.options);

                return (
                    <select
                        className="app-select"
                        value={normalizedValue}
                        onChange={(event) => handleChange(event.target.value)}
                        disabled={disabled}
                        required={field.required}
                    >
                        <option value="">请选择{field.label}</option>
                        {options.map((option, index) => {
                            const optionLabel = typeof option === 'object' ? option.label : option;
                            const optionValue = typeof option === 'object' ? option.value : option;

                            return (
                                <option key={`${field.name}-${index}`} value={optionValue}>
                                    {optionLabel}
                                </option>
                            );
                        })}
                    </select>
                );
            }

            case 'text':
                return (
                    <textarea
                        className="app-textarea"
                        value={normalizedValue}
                        onChange={(event) => handleChange(event.target.value)}
                        placeholder={field.placeholder || `请输入${field.label}`}
                        disabled={disabled}
                        required={field.required}
                    />
                );

            case 'number':
                return (
                    <input
                        type="number"
                        className="app-input"
                        value={normalizedValue}
                        onChange={(event) => handleChange(event.target.value)}
                        placeholder={field.placeholder || `请输入${field.label}`}
                        disabled={disabled}
                        required={field.required}
                    />
                );

            case 'decimal':
                return (
                    <input
                        type="number"
                        step="0.01"
                        className="app-input"
                        value={normalizedValue}
                        onChange={(event) => handleChange(event.target.value)}
                        placeholder={field.placeholder || `请输入${field.label}`}
                        disabled={disabled}
                        required={field.required}
                    />
                );

            case 'date':
                return (
                    <input
                        type="date"
                        className="app-input"
                        value={normalizedValue}
                        onChange={(event) => handleChange(event.target.value)}
                        disabled={disabled}
                        required={field.required}
                    />
                );

            case 'boolean':
                return (
                    <label className="form-field__toggle">
                        <input
                            type="checkbox"
                            checked={normalizedValue === true || normalizedValue === 'true' || normalizedValue === 1}
                            onChange={(event) => handleChange(event.target.checked)}
                            disabled={disabled}
                        />
                        <span>{field.label}</span>
                    </label>
                );

            default:
                return (
                    <input
                        type="text"
                        className="app-input"
                        value={normalizedValue}
                        onChange={(event) => handleChange(event.target.value)}
                        placeholder={field.placeholder || `请输入${field.label}`}
                        disabled={disabled}
                        required={field.required}
                    />
                );
        }
    };

    return (
        <div className={`form-field ${isFullWidth ? 'form-field--full' : ''}`}>
            {field.type !== 'boolean' && (
                <label className="form-field__label">
                    {field.label}
                    {requiredMark}
                </label>
            )}
            {renderControl()}
            {field.validation?.message && (
                <small className="form-field__hint">{field.validation.message}</small>
            )}
        </div>
    );
}

export default FormField;
